import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from '../context/StoreContext.tsx';
import { ClipboardList, Plus, Calendar, ArrowRight, Target, MoreVertical, X, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Campaign } from '../types.ts';

const Campaigns: React.FC = () => {
  const { campaigns, addCampaign } = useStore();
  const [filter, setFilter] = useState<'active' | 'completed'>('active');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    chipRole: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredCampaigns = campaigns.filter(c => 
    filter === 'active' ? c.status === 'active' || c.status === 'planning' : c.status === 'completed' || c.status === 'archived'
  );

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    
    setIsSubmitting(true);
    try {
      const newCampaign: Campaign = {
        id: `camp-${Date.now()}`,
        name: formData.name,
        description: formData.description,
        startDate: formData.startDate || new Date().toISOString().split('T')[0],
        endDate: formData.endDate || new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
        status: 'active',
        assetCount: 0,
        chipRole: formData.chipRole
      };
      
      await addCampaign(newCampaign);
      setIsModalOpen(false);
      setFormData({ name: '', description: '', startDate: '', endDate: '', chipRole: '' });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto h-[calc(100vh-120px)] flex flex-col relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Campaigns</h1>
          <p className="text-[var(--text-secondary)]">Organize Chip content by marketing initiative.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-[var(--text-primary)] text-[var(--bg-primary)] px-4 py-2 rounded-lg font-medium hover:scale-105 transition-transform flex items-center gap-2 shadow-lg"
        >
          <Plus size={16} /> New Campaign
        </button>
      </div>

      <div className="flex gap-6 border-b border-[var(--border-subtle)] mb-6">
        <button 
          onClick={() => setFilter('active')}
          className={`pb-3 text-sm font-medium transition-colors relative ${filter === 'active' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
        >
          Active Campaigns
          {filter === 'active' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--text-primary)] rounded-t-full"></div>}
        </button>
        <button 
          onClick={() => setFilter('completed')}
          className={`pb-3 text-sm font-medium transition-colors relative ${filter === 'completed' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
        >
          Completed
          {filter === 'completed' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--text-primary)] rounded-t-full"></div>}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 pr-2">
        <div className="grid grid-cols-1 gap-4">
            {filteredCampaigns.map(camp => (
            <div key={camp.id} className="bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-xl p-6 hover:shadow-md transition-all group">
                <div className="flex justify-between items-start">
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-[var(--text-primary)]">{camp.name}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                        camp.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 
                        camp.status === 'planning' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }`}>
                        {camp.status}
                    </span>
                    </div>
                    <p className="text-[var(--text-secondary)] mb-6 max-w-2xl">{camp.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-6 text-sm text-[var(--text-secondary)]">
                    <div className="flex items-center gap-2 bg-[var(--bg-tertiary)] px-3 py-1.5 rounded-lg border border-[var(--border-subtle)]">
                        <Calendar size={16} />
                        <span>{camp.startDate} — {camp.endDate}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-[var(--text-primary)]">{camp.assetCount}</span> Assets
                    </div>
                    {camp.chipRole && (
                        <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                        <Target size={16} />
                        Role: <span className="font-medium">{camp.chipRole}</span>
                        </div>
                    )}
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <button className="p-2 hover:bg-[var(--bg-tertiary)] rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] self-end">
                    <MoreVertical size={20} />
                    </button>
                    <Link 
                    to={`/library?campaign=${camp.id}`}
                    className="mt-4 px-4 py-2 text-sm text-[var(--text-primary)] font-medium border border-[var(--border-default)] rounded-lg hover:bg-[var(--bg-secondary)] flex items-center gap-2 group-hover:border-[var(--text-primary)] transition-all"
                    >
                    Open Campaign <ArrowRight size={16} />
                    </Link>
                </div>
                </div>
            </div>
            ))}
            
            {filteredCampaigns.length === 0 && (
            <div className="h-full min-h-[300px] flex flex-col items-center justify-center border-2 border-dashed border-[var(--border-subtle)] rounded-xl bg-[var(--bg-secondary)]">
                <div className="w-16 h-16 bg-[var(--bg-primary)] rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-[var(--border-subtle)]">
                <Target className="text-[var(--text-tertiary)]" size={32} />
                </div>
                <h3 className="text-[var(--text-primary)] font-medium mb-1">No campaigns found</h3>
                <p className="text-[var(--text-secondary)] text-sm mb-4">Create a new campaign to start organizing content.</p>
                <button 
                onClick={() => setIsModalOpen(true)}
                className="btn-primary"
                >
                Create Campaign
                </button>
            </div>
            )}
        </div>
      </div>

      {/* New Campaign Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-lg border border-[var(--border-subtle)] animate-slideUp overflow-hidden">
             <div className="flex items-center justify-between p-5 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                <h3 className="font-bold text-lg text-[var(--text-primary)]">New Campaign</h3>
                <button onClick={() => setIsModalOpen(false)} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1 rounded-full hover:bg-[var(--bg-tertiary)]">
                  <X size={20} />
                </button>
             </div>
             <form onSubmit={handleCreateCampaign} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-bold text-[var(--text-primary)] mb-2">Campaign Name</label>
                  <input 
                    type="text" 
                    autoFocus
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g. Q3 Product Launch"
                    className="input-field"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Start Date</label>
                    <input 
                      type="date" 
                      value={formData.startDate}
                      onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">End Date</label>
                    <input 
                      type="date" 
                      value={formData.endDate}
                      onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                      className="input-field"
                    />
                  </div>
                </div>

                <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Description</label>
                   <textarea 
                     value={formData.description}
                     onChange={(e) => setFormData({...formData, description: e.target.value})}
                     className="input-field"
                     rows={3}
                     placeholder="What is this campaign about?"
                   />
                </div>

                <div>
                   <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Chip's Role (Optional)</label>
                   <input 
                     type="text" 
                     value={formData.chipRole}
                     onChange={(e) => setFormData({...formData, chipRole: e.target.value})}
                     placeholder="e.g. Brand Ambassador at booth"
                     className="input-field"
                   />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border-subtle)] mt-4">
                   <button 
                     type="button" 
                     onClick={() => setIsModalOpen(false)}
                     className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
                   >
                     Cancel
                   </button>
                   <button 
                     type="submit" 
                     disabled={isSubmitting || !formData.name.trim()}
                     className="btn-primary flex items-center gap-2"
                   >
                     {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : null}
                     Create Campaign
                   </button>
                </div>
             </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Campaigns;